import React, { useMemo } from "react";
import { useOutletContext, useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import NavbarScroll from "../../components/subComponents/NavbarScroll";
import { useItemCategoryInfinite } from "../../hooks/useItemCategoryInfinite";
import { ConnectCard1 } from "../../components/subComponents/ConnectCard1";
import { saffronRout, saffronURL } from "../../Url";

const safeArray = (v) => (Array.isArray(v) ? v : []);
const addPosition = (arr) => safeArray(arr).map((item, i) => ({ ...item, position: i }));

const keepKeys = ["country", "state", "city", "language", "category", "category_name"];

const buildSafeSearch = (searchParams) => {
  const next = new URLSearchParams();
  keepKeys.forEach((k) => {
    const v = searchParams.get(k);
    if (v) next.set(k, v);
  });
  return next;
};

const Video = () => {
  const {
    selectedCountry,
    selectedCategory,
    selectedLanguage,
    selectedState,
    selectedCity,
  } = useOutletContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ navbar se aane wala category_name (code)
  const categoryName = searchParams.get("category_name") || "";

  const {
    item: videos,
    ref,
    hasNextPage,
    isFetchingNextPage,
    isPending: videosPending,
  } = useItemCategoryInfinite({
    key: "connect_videos",
    endpoint: "videos",
    listKey: "videos",
    category_name: categoryName,
    selectedCategory,
    selectedCountry,
    selectedLanguage,
    selectedState,
    selectedCity,
    enabled: true,
    threshold: 0.7,
  });

  const hasVideos = videos?.length > 0;
  const getVideos = useMemo(() => addPosition(videos), [videos]);

  // Menu data
  const { data: menuData, isLoading: loadingTree } = useQuery({
    queryKey: [
      "menuData_connect_slide",
      selectedCountry,
      selectedLanguage,
      selectedCategory,
      selectedState,
      selectedCity,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedState) params.append("state", selectedState);
      if (selectedCity) params.append("city", selectedCity);

      const url = `${saffronURL}/api/v1/public/menu?${params.toString()}`;
      const resp = await axios.get(url);
      return resp.data;
    },
    enabled: !!(selectedCountry || selectedLanguage || selectedCategory),
    retry: 2,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60, // ✅ react-query v5 (cacheTime -> gcTime)
    refetchOnWindowFocus: false,
  });

  const navbaritems = useMemo(() => {
    return menuData?.data?.items?.find((item) => item.key === "video");
  }, [menuData]);

  // ✅ category change => URL update => list refetch
  const handleCategorySelect = (child) => {
    const params = new URLSearchParams(searchParams);

    if (child?.code) params.set("category_name", child.code);
    else params.delete("category_name");

    setSearchParams(params, { replace: false });
    window.scrollTo(0, 0);
  };

  // ✅ CARD CLICK => NAVIGATE TO DETAILS (IMPORTANT)
  const handleOpenDetails = (item) => {
    const slug = item?.slug || item?.post_slug || item?.id;
    if (!slug) return;

    const next = buildSafeSearch(searchParams);
    next.set("section", "videos"); // ✅ prev/next list key

    navigate(
      {
        pathname: `${saffronRout}/details/videos/${slug}`,
        search: `?${next.toString()}`,
      },
      {
        state: {
          sectionKey: "videos",
          position: item?.position ?? 0,
          label: item?.title || "Video",
        },
      }
    );

    window.scrollTo(0, 0);
  };

  return (
    <>
      <NavbarScroll
        items={navbaritems}
        activeCode={categoryName}
        onSelect={handleCategorySelect}
        loading={loadingTree}
      />

      <section className="py-3 px-1 md:px-5 lg:px-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {getVideos?.map((item) => (
            <ConnectCard1
              key={item.id}
              item={item}
              onArticleClick={() => handleOpenDetails(item)}
                 imgH=" h-[230px]"
              objimg="object-fill"
            />
          ))}
        </div>

        {videosPending && <p className="text-gray-500">Loading videos…</p>}

        {!videosPending && !hasVideos && (
          <p className="text-gray-400 italic">
            No videos found in <span className="font-medium">'Videos'</span>
          </p>
        )}

        {hasNextPage && <div ref={ref} className="h-10" />}

        {isFetchingNextPage && (
          <div className="text-center text-sm text-gray-500 py-4">
            Loading more...
          </div>
        )}
      </section>
    </>
  );
};

export default Video;